import LockOutlined from '@mui/icons-material/LockOutlined';
import { Box, Paper, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha, styled } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { motion, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';

import forbiddenAccessImage from './assets/forbidden-access-image.png';

export type NoAccessStateProps = {
  variant?: 'page' | 'section';
  title?: string;
  description?: string;
  action?: ReactNode;
  sx?: SystemStyleObject<Theme>;
};

const IconContainer = styled(Box)(({ theme }) => ({
  width: 72,
  height: 72,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  border: `2px dashed ${alpha(theme.palette.primary.main, 0.24)}`,
  ...theme.applyStyles('dark', {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    border: `2px dashed ${alpha(theme.palette.primary.main, 0.32)}`,
  }),
}));

export default function NoAccessState({
  variant = 'page',
  title = 'Access Restricted',
  description = "You don't have permission to view this content. Contact your administrator if you need access.",
  action,
  sx,
}: NoAccessStateProps) {
  const isPage = variant === 'page';
  const shouldReduceMotion = useReducedMotion();
  const baseSx: SystemStyleObject<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    px: 3,
    py: isPage ? 8 : 4,
  };

  return (
    <motion.div
      initial={
        shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }
      }
      animate={
        shouldReduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }
      }
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <Paper elevation={0} role="status" sx={sx ? [baseSx, sx] : baseSx}>
        {isPage ? (
          <Box
            component="img"
            src={forbiddenAccessImage}
            alt=""
            sx={{
              maxWidth: 'min(320px, 70vw)',
              maxHeight: 240,
              objectFit: 'contain',
              mb: 3,
            }}
          />
        ) : (
          <IconContainer>
            <LockOutlined sx={{ fontSize: 32 }} />
          </IconContainer>
        )}

        <Typography
          variant={isPage ? 'h2' : 'h4'}
          component="p"
          fontWeight={600}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, maxWidth: 480 }}
        >
          {description}
        </Typography>

        {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
      </Paper>
    </motion.div>
  );
}
